import { ImportingTaskContext } from "./importing-queue"
import { PreviewLoader } from "@/lib/metadata/metadata-loader"
import { fetchOriginalPreview } from "@/storage/indexeddb"
import { getFileForPhoto } from "../files-state"
import {
	resizeAndStoreSmallPreview,
	updatePreviews,
	visibleThumbnailsAtom,
} from "../previews/previews-state"
import { ProjectItem } from "../project/projects-service"
import { CatalogManager, CullingInfoRecord } from "@/lib/catalog"
import { hasNoEmbeddedPreview } from "@/lib/catalog/utils"
import { PreviewInfo, PreviewSize, PreviewTag } from "@/store/previews/previews-types"
import {
	cullingInfoAtom,
	currentPhotoIdAtom,
	getCullingInfo,
	sortedPhotosAtom,
	updateCullingInfos,
	photosAtom,
	getPhotoMetadata,
} from "../photo/photos-state"
import { store } from "../store"
import { Culler } from "@/lib/culling/culler"
import { CULLER_BATCH_SIZE } from "@/lib/culling/culler-connection"
import { createOrientedBitmap } from "@/core/utils/load-image"
import {
	clusterOnTheFlyForCullingAndEditing,
	mergeClusters,
	getSortedPhotosAndIndexMap,
	logClusterSummary,
	normalizeCullingClusterRuns,
} from "@/lib/ai/ai-cluster"
import { Sentry } from "@/telemetry/sentry"
import { ProcessingPerformance } from "@/telemetry/processing-performance"
import {
	updateKeepersAndRejects,
	isSuggestingKeepersOrRejectsAtom,
	suggestedKeeperCountAtom,
	extractFacesFromPreview,
	ClusterThreshold,
	completedCullingPhotoIdsAtom,
} from "./culling-state"
import { projectAtom } from "../project/projects-state"
import { PhotoId } from "../undoable-state"
import { photosProcessingPriorityAtom } from "../processing/processing-queue-priority"
import {
	beginTaskPerformanceMeasurement,
	beginTaskSingleItemPerformanceMeasurement,
	commitTaskPerformanceMeasurement,
	commitTaskSingleItemPerformanceMeasurement,
} from "../debug/task-performance-state"
import PQueue from "p-queue"
import { padFaceRect } from "./culling-utils"
import {
	commitTrackingPerformanceValue,
	startTrackingPerformanceValue,
} from "@/utils/performance-stats"
import { CullingInputDimension, logCullingInfo } from "@/lib/ai/utils"
import { transfer } from "@/utils/peer"
import { AI } from "@/lib/ai/ai"

/** Create a default culling info for photos that failed to extract preview/culling data */
export function createDefaultCullingInfo(photoId: string): CullingInfoRecord {
	return {
		photoId,
		faceInfo: [],
		scoreItem: {
			lighting: 0,
			blur: 0,
			overallFloat: 0,
			overall: 0,
		},
		clusterLeaderIdMap: {
			[ClusterThreshold.Similar]: photoId,
			[ClusterThreshold.Exact]: photoId,
			[ClusterThreshold.Loose]: photoId,
		},
	}
}

interface PreparedCullingData {
	cullingInfo: CullingInfoRecord
	preview?: Blob
	shouldApply: boolean
}

interface PreparedCullingResult {
	info: PreparedCullingData
	score: PreparedCullingData
}

const CULLING_APPLY_BATCH_SIZE = 20

class CullingImporter {
	// How often we are going to flush changes to the store to update the UI
	private bufferFlushToUILimit = 10

	// Queues to manage difference processing
	private renderedPreviewCullingQueue = new PQueue({ concurrency: 1, autoStart: true })
	private cullingFaceExtractionQueue = new PQueue({ concurrency: 1, autoStart: true })
	private embeddedPreviewCullingQueue = new PQueue({
		concurrency: 2,
		autoStart: true,
	})

	// Buffers to collect data before flushing to the UI

	private collectedCullingInfoBuffer = new Map<PhotoId, CullingInfoRecord>()

	// External data
	public startProjectId: string = ""
	private isInProgress = false
	private performance?: ProcessingPerformance

	async start(project: ProjectItem) {
		if (this.startProjectId !== project.id) {
			this.cancel()
			this.isInProgress = false
		}

		if (this.isInProgress === true) {
			return
		}

		this.startProjectId = project.id
		this.isInProgress = true
		this.performance?.finish("canceled")
		this.performance = new ProcessingPerformance("culling", { projectId: project.id })
		store.set(completedCullingPhotoIdsAtom, new Set())

		// Fetching existing culling info from the catalog
		const cullingInfoRecords = await CatalogManager.fetchCullingInfoRecords()
		store.set(cullingInfoAtom, cullingInfoRecords)

		const allPhotos = store.get(sortedPhotosAtom)

		// Setting collected buffer according to the amount of photos.
		this.bufferFlushToUILimit = allPhotos.length > 4500 ? 15 : 10

		this.renderedPreviewCullingQueue.start()
		this.cullingFaceExtractionQueue.start()
		this.embeddedPreviewCullingQueue.start()
	}

	cancel() {
		this.performance?.finish("canceled")
		this.performance = undefined
		this.embeddedPreviewCullingQueue.clear()
		this.renderedPreviewCullingQueue.clear()
		this.cullingFaceExtractionQueue.clear()
		store.set(completedCullingPhotoIdsAtom, new Set())

		void Culler.cleanupModels()
		this.isInProgress = false
		this.startProjectId = ""
	}

	async pauseAndWaitForTasksToComplete() {
		// Stop culling tasks before face extraction because they can schedule face tasks.
		this.embeddedPreviewCullingQueue.pause()
		await this.embeddedPreviewCullingQueue.onPendingZero()
		this.embeddedPreviewCullingQueue.clear()

		this.renderedPreviewCullingQueue.pause()
		await this.renderedPreviewCullingQueue.onPendingZero()
		this.renderedPreviewCullingQueue.clear()

		this.cullingFaceExtractionQueue.pause()
		await this.cullingFaceExtractionQueue.onPendingZero()
		this.cullingFaceExtractionQueue.clear()
		store.set(completedCullingPhotoIdsAtom, new Set())
	}

	/**
	 * Async cancel that waits for in-flight tasks to complete before cleanup.
	 * Use this when you need to ensure no tasks are running after the call returns.
	 * @param cleanupModels - Whether to cleanup Culler models (default: true). Set to false when coordinating with other importers that share the same models.
	 */
	async pauseAndCancel({ cleanupModels = true } = {}) {
		const performance = this.performance
		this.performance = undefined
		this.isInProgress = false
		await this.pauseAndWaitForTasksToComplete()
		performance?.finish("canceled")

		if (cleanupModels) {
			void Culler.cleanupModels()
		}
		this.startProjectId = ""
	}

	async clearTasks() {
		// Pause queues to ensure no tasks are currently running
		await this.pauseAndWaitForTasksToComplete()
		this.flushCollectedCullingInfoBuffer()
	}

	setupSubscribers() {
		// On scroll/visibility, boost visible photos on top of their base priority
		store.sub(photosProcessingPriorityAtom, () => {
			const allPhotos = Object.values(store.get(photosAtom))
			const { sortedPhotos, idToIndex } = getSortedPhotosAndIndexMap(allPhotos)
			const currentPhotoId = store.get(currentPhotoIdAtom)
			const visiblePhotoIds = store.get(visibleThumbnailsAtom)
			const priorityQueues = [
				this.embeddedPreviewCullingQueue,
				this.renderedPreviewCullingQueue,
				this.cullingFaceExtractionQueue,
			]

			// Boost visible photos
			for (const photoId of visiblePhotoIds) {
				const base = sortedPhotos.length - (idToIndex[photoId] ?? 0)
				for (const queue of priorityQueues) {
					try {
						queue.setPriority(photoId, base + 1000)
					} catch {
						// Task is not pending in this queue.
					}
				}
			}

			// Boost current photo (highest priority)
			if (currentPhotoId) {
				const base = sortedPhotos.length - (idToIndex[currentPhotoId] ?? 0)
				for (const queue of priorityQueues) {
					try {
						queue.setPriority(currentPhotoId, base + 10000)
					} catch {
						// Task is not pending in this queue.
					}
				}
			}
		})
	}

	async startCulling() {
		if (!this.isInProgress) return

		const projectId = this.startProjectId
		const performance = this.performance
		const { sortedPhotos, idToIndex } = getSortedPhotosAndIndexMap(
			Object.values(store.get(photosAtom)),
		)
		const contexts: ImportingTaskContext[] = sortedPhotos.flatMap((photo) => {
			const metadata = getPhotoMetadata(photo.id)
			return metadata ? [{ photo, metadata, cullingInfo: getCullingInfo(photo.id) }] : []
		})
		if (contexts.length === 0) return

		beginTaskPerformanceMeasurement("culling-group")
		beginTaskPerformanceMeasurement("culling-score")
		this.cullingFaceExtractionQueue.start()
		for (let offset = 0; offset < contexts.length; offset += CULLING_APPLY_BATCH_SIZE) {
			const batch = contexts.slice(offset, offset + CULLING_APPLY_BATCH_SIZE)
			const preparedResults = new Map<PhotoId, PreparedCullingResult>()
			this.renderedPreviewCullingQueue.pause()
			this.embeddedPreviewCullingQueue.pause()

			const embeddedContexts: ImportingTaskContext[] = []
			const renderedContexts: ImportingTaskContext[] = []
			for (const context of batch) {
				if (
					context.extractedPreview != undefined ||
					!hasNoEmbeddedPreview(context.metadata)
				)
					embeddedContexts.push(context)
				else renderedContexts.push(context)
			}
			const queuedBatches: Array<{
				contexts: ImportingTaskContext[]
				embedded: boolean
			}> = renderedContexts.map((context) => ({ contexts: [context], embedded: false }))
			for (let index = 0; index < embeddedContexts.length; index += CULLER_BATCH_SIZE) {
				queuedBatches.push({
					contexts: embeddedContexts.slice(index, index + CULLER_BATCH_SIZE),
					embedded: true,
				})
			}

			for (const queuedBatch of queuedBatches) {
				const queue = queuedBatch.embedded
					? this.embeddedPreviewCullingQueue
					: this.renderedPreviewCullingQueue
				const taskId = (queuedBatch.contexts[0] as ImportingTaskContext).photo.id
				const basePriority = Math.max(
					...queuedBatch.contexts.map(
						(context) => sortedPhotos.length - (idToIndex[context.photo.id] ?? 0),
					),
				)

				void queue
					.add(
						async () => {
							try {
								if (!this.isInProgress || projectId !== this.startProjectId) return
								let prepared: PreparedCullingResult[] | undefined
								try {
									prepared = await this.prepareCullingBatch(
										queuedBatch.contexts,
										projectId,
										performance,
									)
								} catch (error) {
									if (queuedBatch.contexts.length === 1) throw error
									Sentry.captureException(error)
									prepared = []
									for (const context of queuedBatch.contexts) {
										try {
											const single = await this.prepareCullingBatch(
												[context],
												projectId,
												performance,
											)
											if (!single?.[0]) return
											prepared.push(single[0])
										} catch (singleError) {
											Sentry.captureException(singleError)
											const cullingInfo = createDefaultCullingInfo(
												context.photo.id,
											)
											if (
												(getCullingInfo(context.photo.id) ??
													context.cullingInfo) == undefined
											)
												performance?.failed()
											prepared.push({
												info: { cullingInfo, shouldApply: true },
												score: { cullingInfo, shouldApply: false },
											})
										}
									}
								}
								if (
									!prepared ||
									prepared.length !== queuedBatch.contexts.length ||
									!this.isInProgress ||
									projectId !== this.startProjectId
								)
									return

								queuedBatch.contexts.forEach((context, index) => {
									preparedResults.set(
										context.photo.id,
										prepared[index] as PreparedCullingResult,
									)
								})
								store.set(completedCullingPhotoIdsAtom, (photoIds) => {
									const completed = new Set(photoIds)
									queuedBatch.contexts.forEach((context) =>
										completed.add(context.photo.id),
									)
									return completed
								})
							} catch (error) {
								Sentry.captureException(error)
								if (!this.isInProgress || projectId !== this.startProjectId) return
								const completed = new Set(store.get(completedCullingPhotoIdsAtom))
								for (const context of queuedBatch.contexts) {
									const cullingInfo = createDefaultCullingInfo(context.photo.id)
									if (
										(getCullingInfo(context.photo.id) ?? context.cullingInfo) ==
										undefined
									)
										performance?.failed()
									preparedResults.set(context.photo.id, {
										info: { cullingInfo, shouldApply: true },
										score: { cullingInfo, shouldApply: false },
									})
									completed.add(context.photo.id)
								}
								store.set(completedCullingPhotoIdsAtom, completed)
							}
						},
						{
							id: taskId,
							priority:
								basePriority +
								(queuedBatch.contexts.some(
									(context) => context.photo.id === store.get(currentPhotoIdAtom),
								)
									? 1_000_000
									: 0),
						},
					)
					.catch((error) => Sentry.captureException(error))
			}

			this.renderedPreviewCullingQueue.start()
			this.embeddedPreviewCullingQueue.start()
			await Promise.all([
				this.renderedPreviewCullingQueue.onIdle(),
				this.embeddedPreviewCullingQueue.onIdle(),
			])
			if (
				!this.isInProgress ||
				projectId !== this.startProjectId ||
				preparedResults.size !== batch.length
			)
				return

			for (const context of batch) {
				const prepared = preparedResults.get(context.photo.id)
				if (!prepared) return
				const cullingInfo = this.applyCullingInfo(prepared.info, projectId)
				if (!cullingInfo) return
				const scoredCullingInfo = this.applyCullingScoring(
					prepared.score,
					cullingInfo,
					projectId,
				)
				if (!scoredCullingInfo) return
				void this.extractFacePreviews(
					{
						...context,
						extractedPreview:
							prepared.score.preview ??
							prepared.info.preview ??
							context.extractedPreview,
						cullingInfo: scoredCullingInfo,
					},
					projectId,
				)
			}

			this.flushCollectedCullingInfoBuffer()
			if (store.get(isSuggestingKeepersOrRejectsAtom))
				void updateKeepersAndRejects(store.get(suggestedKeeperCountAtom))
		}

		commitTaskPerformanceMeasurement("culling-group")
		this.mergeClustersAndUpdateBuffer()
		this.normalizeCullingClusterRunsAndUpdateBuffer()
		this.flushCollectedCullingInfoBuffer()
		commitTaskPerformanceMeasurement("culling-score")

		if (store.get(isSuggestingKeepersOrRejectsAtom))
			void updateKeepersAndRejects(store.get(suggestedKeeperCountAtom))
		console.log(`Culling queue finished! ${contexts.length} / ${sortedPhotos.length}`)
		const finalCullingInfos = store.get(cullingInfoAtom)
		logClusterSummary({
			cullingInfos: finalCullingInfos,
			threshold: ClusterThreshold.Similar,
			prefix: "[cullingGrouping]",
		})
		void Culler.cleanupModels()
			.catch((error) => Sentry.captureException(error))
			.then(() => Culler.reset())
			.catch((error) => Sentry.captureException(error))
		performance?.finish("completed")
		if (this.performance === performance) this.performance = undefined
		this.isInProgress = false

		// Uncomment to run evaluation with folder "culling-full-image-eval-raw" when culling completes:
		// void import("@/utils/culling-evaluation").then((m) => m.runAndLogEvaluation(true))
	}

	/* Private methods */

	private flushCollectedCullingInfoBuffer() {
		if (this.collectedCullingInfoBuffer.size === 0) {
			return
		}

		// Atomically swap buffer to prevent race condition:
		// Capture current buffer and replace with empty one before processing
		const bufferToFlush = this.collectedCullingInfoBuffer
		this.collectedCullingInfoBuffer = new Map<PhotoId, CullingInfoRecord>()

		updateCullingInfos((infos) => {
			bufferToFlush.forEach((value, id) => {
				infos[id] = value
			})
		}, true)
	}

	public hasCullingInfo(photoId: PhotoId) {
		return getCullingInfo(photoId) != undefined || this.collectedCullingInfoBuffer.has(photoId)
	}

	public getBufferedCullingInfo(photoId: PhotoId): CullingInfoRecord | undefined {
		return this.collectedCullingInfoBuffer.get(photoId)
	}

	private async prepareCullingBatch(
		contexts: ImportingTaskContext[],
		projectId: string,
		performance?: ProcessingPerformance,
	): Promise<PreparedCullingResult[] | undefined> {
		const states = contexts.map((context, index) => {
			const cullingInfo = getCullingInfo(context.photo.id) ?? context.cullingInfo
			return {
				context,
				index,
				cullingInfo,
				needsInfo: cullingInfo == undefined,
				needsScore: cullingInfo?.scoreItem == undefined,
			}
		})
		if (states.some(({ needsInfo }) => needsInfo)) performance?.start()
		const measurements = states.map(({ context, needsInfo, needsScore }) => {
			if (needsInfo) startTrackingPerformanceValue(context.photo.id, "cullingInfo")
			if (needsScore) startTrackingPerformanceValue(context.photo.id, "cullingScoring")
			return {
				photoId: context.photo.id,
				info: needsInfo ? beginTaskSingleItemPerformanceMeasurement("culling-group") : null,
				score: needsScore
					? beginTaskSingleItemPerformanceMeasurement("culling-score")
					: null,
				needsInfo,
				needsScore,
			}
		})

		try {
			if (store.get(projectAtom)?.id !== projectId || this.startProjectId !== projectId)
				return

			const prepared = new Array<PreparedCullingResult>(contexts.length)
			for (const state of states) {
				if (!state.needsInfo && !state.needsScore && state.cullingInfo) {
					const data = {
						cullingInfo: state.cullingInfo,
						preview: state.context.extractedPreview,
						shouldApply: false,
					}
					prepared[state.index] = { info: data, score: data }
				}
			}

			const previews = await Promise.all(
				states
					.filter(({ needsInfo, needsScore }) => needsInfo || needsScore)
					.map(async (state) => ({
						...state,
						preview:
							state.context.extractedPreview ??
							(await this.getOrExtractPreviewDataForCulling(
								state.context,
								projectId,
							)),
					})),
			)
			if (!this.isInProgress || this.startProjectId !== projectId) return

			const ready = previews.flatMap((state) =>
				state.preview ? [{ ...state, preview: state.preview }] : [],
			)
			for (const state of previews) {
				if (state.preview) continue
				console.error(
					`No preview for culling extraction: ${state.context.photo.fileName}, using default`,
				)
				const info = state.cullingInfo ?? createDefaultCullingInfo(state.context.photo.id)
				const score = state.cullingInfo
					? {
							...state.cullingInfo,
							scoreItem: { lighting: 0, blur: 0, overallFloat: 0, overall: 0 },
						}
					: info
				prepared[state.index] = {
					info: { cullingInfo: info, shouldApply: state.needsInfo },
					score: {
						cullingInfo: score,
						shouldApply: state.cullingInfo != undefined && state.needsScore,
					},
				}
			}

			if (ready.length > 0) {
				const bitmapResults = await Promise.allSettled(
					ready.map(({ context, preview }) =>
						createOrientedBitmap(preview, context.metadata?.orientation ?? null),
					),
				)
				const failedBitmap = bitmapResults.find(
					(result): result is PromiseRejectedResult => result.status === "rejected",
				)
				if (failedBitmap) {
					for (const result of bitmapResults)
						if (result.status === "fulfilled") result.value.close()
					throw failedBitmap.reason
				}
				const bitmaps = bitmapResults.flatMap((result) =>
					result.status === "fulfilled" ? [result.value] : [],
				)

				try {
					const results = await Culler.extractCullingData(
						transfer(
							ready.map((state, index) => ({
								photoId: state.context.photo.id,
								inputImage: bitmaps[index] as ImageBitmap,
								...(state.cullingInfo
									? { existedCullingInfo: state.cullingInfo }
									: {}),
								extractScores: state.needsScore,
							})),
							bitmaps,
						),
					)
					if (results.length !== ready.length)
						throw new Error("Culler returned an incomplete batch")

					ready.forEach((state, index) => {
						const result = results[index]
						if (!result) throw new Error("Culler returned no result")
						prepared[state.index] = {
							info: {
								cullingInfo: result.info,
								preview: state.preview,
								shouldApply: state.needsInfo,
							},
							score: {
								cullingInfo: result.score,
								preview: state.preview,
								shouldApply: state.needsScore,
							},
						}
					})
				} finally {
					bitmaps.forEach((bitmap) => bitmap.close())
				}
			}

			const completed = contexts.map((_, index) => {
				const result = prepared[index]
				if (!result) throw new Error("Missing prepared culling result")
				return result
			})
			for (const state of states) {
				if (!state.needsInfo) continue
				const result = completed[state.index] as PreparedCullingResult
				if (result.info.preview) performance?.processed(state.context.photo.fileName)
				else performance?.failed()
			}
			return completed
		} finally {
			for (const measurement of measurements) {
				commitTaskSingleItemPerformanceMeasurement(measurement.info)
				commitTaskSingleItemPerformanceMeasurement(measurement.score)
				if (measurement.needsInfo)
					commitTrackingPerformanceValue(measurement.photoId, "cullingInfo")
				if (measurement.needsScore)
					commitTrackingPerformanceValue(measurement.photoId, "cullingScoring")
			}
		}
	}

	private async prepareCullingInfo(
		context: ImportingTaskContext,
		projectId: string,
	): Promise<PreparedCullingData | undefined> {
		const { photo, extractedPreview, metadata } = context

		const cullingInfo = getCullingInfo(photo.id)

		if (cullingInfo) {
			return {
				cullingInfo,
				preview: extractedPreview,
				shouldApply: false,
			}
		}

		if (store.get(projectAtom)?.id !== projectId || this.startProjectId !== projectId) {
			//project closed or switched project, no need to store any more
			return
		}

		const measurementTask = beginTaskSingleItemPerformanceMeasurement("culling-group")
		startTrackingPerformanceValue(photo.id, "cullingInfo")
		let preview: Blob | undefined

		try {
			preview =
				extractedPreview ??
				(await this.getOrExtractPreviewDataForCulling(context, projectId))

			if (!preview) {
				// No preview available, use default culling info to avoid blocking culling grouping
				console.error(
					`No preview for culling info extraction: ${photo.fileName}, using default`,
				)
				return {
					cullingInfo: createDefaultCullingInfo(photo.id),
					shouldApply: true,
				}
			} else {
				// get orientation from metadata; it's fine if not exists,
				// just takes a bit more time (~1ms) to call removeBitmapOrientation even when not needed
				const bitmap = await createOrientedBitmap(preview, metadata?.orientation ?? null)
				let cullingData: CullingInfoRecord

				try {
					const [result] = await Culler.extractCullingData(
						transfer(
							[{ photoId: photo.id, inputImage: bitmap, extractScores: false }],
							[bitmap],
						),
					)
					if (!result) throw new Error("Culler returned no result")
					cullingData = result.info
				} finally {
					bitmap.close()
				}

				logCullingInfo(`[cullingImporter] Extracted culling info for ${photo.id}`)
				if (this.startProjectId !== projectId) return
				return {
					cullingInfo: cullingData,
					preview,
					shouldApply: true,
				}
			}
		} catch (error) {
			Sentry.captureException(error, {
				tags: {
					description: "Error extracting culling info",
				},
			})
			console.error(`Error extracting culling info for ${photo.fileName}`, error)
			if (this.startProjectId !== projectId) return
			return {
				cullingInfo: createDefaultCullingInfo(photo.id),
				shouldApply: true,
			}
		} finally {
			commitTaskSingleItemPerformanceMeasurement(measurementTask)
			commitTrackingPerformanceValue(photo.id, "cullingInfo")
		}
	}

	private applyCullingInfo(prepared: PreparedCullingData, projectId: string) {
		if (this.startProjectId !== projectId) return

		let cullingInfo = prepared.cullingInfo
		if (prepared.shouldApply) {
			if (!cullingInfo.clusterLeaderIdMap) {
				cullingInfo = clusterOnTheFlyForCullingAndEditing(cullingInfo, {
					...store.get(cullingInfoAtom),
					...Object.fromEntries(this.collectedCullingInfoBuffer.entries()),
				})
			}

			this.collectedCullingInfoBuffer.set(cullingInfo.photoId, cullingInfo)
			if (this.collectedCullingInfoBuffer.size >= this.bufferFlushToUILimit) {
				this.flushCollectedCullingInfoBuffer()
				if (store.get(isSuggestingKeepersOrRejectsAtom))
					void updateKeepersAndRejects(store.get(suggestedKeeperCountAtom))
			}
		}

		return cullingInfo
	}

	async extractCullingInfo(
		context: ImportingTaskContext,
		projectId: string = this.startProjectId,
	) {
		const prepared = await this.prepareCullingInfo(context, projectId)
		if (!prepared) return
		this.applyCullingInfo(prepared, projectId)
		return prepared.preview
	}

	private async prepareCullingScoring(
		context: ImportingTaskContext,
		projectId: string,
	): Promise<PreparedCullingData | undefined> {
		const { photo, extractedPreview, metadata, cullingInfo } = context
		if (this.startProjectId !== projectId) return

		if (cullingInfo?.scoreItem != undefined) {
			return { cullingInfo, preview: extractedPreview, shouldApply: false }
		}

		if (store.get(projectAtom)?.id !== projectId || this.startProjectId !== projectId) {
			//project closed or switched project, no need to store any more
			return
		}

		const measurementTask = beginTaskSingleItemPerformanceMeasurement("culling-score")
		startTrackingPerformanceValue(photo.id, "cullingScoring")

		let preview: Blob | undefined
		try {
			preview =
				extractedPreview ??
				(await this.getOrExtractPreviewDataForCulling(context, projectId))
			if (this.startProjectId !== projectId) return

			let cullingData: CullingInfoRecord

			if (!preview) {
				// No preview available, use default or existing culling info with default scores
				console.error(
					`No preview for culling scores extraction: ${photo.fileName}, using default`,
				)
				cullingData = {
					...(cullingInfo ?? createDefaultCullingInfo(photo.id)),
					scoreItem: {
						lighting: 0,
						blur: 0,
						overallFloat: 0,
						overall: 0,
					},
				}
			} else {
				const bitmap = await createOrientedBitmap(preview, metadata?.orientation ?? null)

				try {
					const [result] = await Culler.extractCullingData(
						transfer(
							[
								{
									photoId: photo.id,
									inputImage: bitmap,
									existedCullingInfo: cullingInfo,
									extractScores: true,
								},
							],
							[bitmap],
						),
					)
					if (!result) throw new Error("Culler returned no result")
					cullingData = result.score
				} finally {
					bitmap.close()
				}
				if (this.startProjectId !== projectId) return
			}

			if (this.startProjectId !== projectId) return
			return { cullingInfo: cullingData, preview, shouldApply: true }
		} catch (error) {
			Sentry.captureException(error, {
				tags: {
					description: "Error extracting culling scoring",
				},
			})

			console.error(`Error extracting culling scoring for ${photo.id}`, error)
			if (this.startProjectId !== projectId) return
			return {
				cullingInfo: {
					...(cullingInfo ?? createDefaultCullingInfo(photo.id)),
					scoreItem: {
						lighting: 0,
						blur: 0,
						overallFloat: 0,
						overall: 0,
					},
				},
				shouldApply: true,
			}
		} finally {
			commitTaskSingleItemPerformanceMeasurement(measurementTask)
			commitTrackingPerformanceValue(photo.id, "cullingScoring")
		}
	}

	private applyCullingScoring(
		prepared: PreparedCullingData,
		clusteredInfo: CullingInfoRecord,
		projectId: string,
	) {
		if (this.startProjectId !== projectId) return

		let cullingInfo = prepared.cullingInfo
		if (prepared.shouldApply) {
			if (clusteredInfo.clusterLeaderIdMap)
				cullingInfo = {
					...cullingInfo,
					clusterLeaderIdMap: clusteredInfo.clusterLeaderIdMap,
				}

			if (!cullingInfo.clusterLeaderIdMap)
				cullingInfo = clusterOnTheFlyForCullingAndEditing(cullingInfo, {
					...store.get(cullingInfoAtom),
					...Object.fromEntries(this.collectedCullingInfoBuffer.entries()),
				})

			this.collectedCullingInfoBuffer.set(cullingInfo.photoId, cullingInfo)
			if (this.collectedCullingInfoBuffer.size >= this.bufferFlushToUILimit) {
				this.mergeClustersAndUpdateBuffer()
				this.flushCollectedCullingInfoBuffer()
				if (store.get(isSuggestingKeepersOrRejectsAtom))
					void updateKeepersAndRejects(store.get(suggestedKeeperCountAtom))
			}
		}

		return cullingInfo
	}

	async extractCullingScoring(
		context: ImportingTaskContext,
		projectId: string = this.startProjectId,
	) {
		const prepared = await this.prepareCullingScoring(context, projectId)
		if (!prepared || !context.cullingInfo) return
		const cullingInfo = this.applyCullingScoring(prepared, context.cullingInfo, projectId)
		if (!cullingInfo) return
		void this.extractFacePreviews(
			{
				...context,
				extractedPreview: prepared.preview ?? context.extractedPreview,
				cullingInfo,
			},
			projectId,
		)
	}

	extractFacePreviews(context: ImportingTaskContext, projectId: string = this.startProjectId) {
		void this.cullingFaceExtractionQueue.add(
			async () => {
				if (this.startProjectId !== projectId) return
				const { photo, extractedPreview, metadata, cullingInfo } = context

				if (!cullingInfo || !extractedPreview) {
					return
				}

				if (cullingInfo.faceInfo.length === 0) {
					return
				}

				const faceRects =
					cullingInfo?.faceInfo.map((val) => padFaceRect(val.faceRect)) ?? []

				const bitmap = await createOrientedBitmap(
					extractedPreview,
					metadata?.orientation ?? null,
				)

				try {
					await extractFacesFromPreview(projectId, photo, bitmap, faceRects)
				} finally {
					bitmap.close()
				}

				if (
					this.startProjectId === projectId &&
					photo.id === store.get(currentPhotoIdAtom)
				) {
					updateCullingInfos((infos) => {
						infos[photo.id] = cullingInfo
					}, true)
				}
			},
			{
				id: context.photo.id,
				priority:
					context.photo.id === store.get(currentPhotoIdAtom) ? 1_000_000 : undefined,
			},
		)

		if (this.cullingFaceExtractionQueue.isPaused) {
			this.cullingFaceExtractionQueue.start()
		}
	}

	mergeClustersAndUpdateBuffer() {
		const updatedClusters = mergeClusters({
			...store.get(cullingInfoAtom),
			...Object.fromEntries(this.collectedCullingInfoBuffer.entries()),
		})

		for (const [id, value] of Object.entries(updatedClusters)) {
			this.collectedCullingInfoBuffer.set(id, value)
		}
	}

	normalizeCullingClusterRunsAndUpdateBuffer() {
		const updatedClusters = normalizeCullingClusterRuns({
			...store.get(cullingInfoAtom),
			...Object.fromEntries(this.collectedCullingInfoBuffer.entries()),
		})

		for (const [id, value] of Object.entries(updatedClusters)) {
			this.collectedCullingInfoBuffer.set(id, value)
		}
	}

	getOrExtractPreviewDataForCulling = async (
		context: ImportingTaskContext,
		projectId: string = this.startProjectId,
	) => {
		const photo = context.photo

		const previewRecord = await fetchOriginalPreview({
			projectId,
			photoId: photo.id,
			size: PreviewSize.medium,
		})
		if (this.startProjectId !== projectId) return

		// Only return extracted preview (not rendered raw) if it exists in storage
		if (previewRecord?.data && previewRecord?.tags.includes("extracted")) {
			return previewRecord?.data
		}

		let previewData: Blob | undefined
		let renderedOrExtracted: PreviewTag = "extracted"
		let renderedFullSize: [number, number] | undefined

		const file = getFileForPhoto(photo)
		const metadata = getPhotoMetadata(photo.id) ?? context.metadata

		if (file) {
			previewData =
				(await PreviewLoader.getImage(file, PreviewSize.medium, CullingInputDimension)) ??
				undefined
		}
		if (this.startProjectId !== projectId) return

		if (!previewData && previewRecord?.data) {
			return previewRecord.data
		}

		if (!previewData) {
			// If still no preview (wow, very deep), render directly with the foreground AI editor.
			// SLOW PAINFUL ROUTE TO EXTRACT THUMBNAIL VIA RENDERING DIRECTLY
			if (file) {
				try {
					await AI.lockEditor()
					try {
						await AI.loadImage(file, { metadata })
						previewData =
							(await AI.generatePreview({
								colorSpace: "display-p3",
								resizeWidth: CullingInputDimension,
								resizeHeight: CullingInputDimension,
							})) ?? undefined
						const [width, height] = await AI.getLoadedImageSize()
						renderedFullSize = [width, height]
					} finally {
						await AI.unlockEditor()
					}
				} catch (error) {
					console.error(`Failed to render culling preview for ${photo.fileName}`, error)
					Sentry.captureException(error, {
						tags: { description: "Error rendering culling preview" },
					})
				}
			}
			renderedOrExtracted = "rendered"
		}
		if (this.startProjectId !== projectId) return

		if (previewData && !previewRecord?.data) {
			const previewInfo: PreviewInfo = {
				projectId,
				photoId: photo.id,
				data: previewData,
				size: PreviewSize.medium,
				tags: [renderedOrExtracted, "original"],
			}
			void updatePreviews({ previewsInfo: [previewInfo] })
			if (renderedFullSize) {
				void resizeAndStoreSmallPreview(previewInfo, renderedFullSize)
			}
		}

		return previewData
	}
}

export const cullingImporter = new CullingImporter()
